import { CheckCircleOutlineRounded, HighlightOffRounded, SendRounded } from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getProduct, RESPONSE_CHECK_CODE, useCouponQuery } from "../../../../../../api/services";
import { IFormValues } from "../../../../types";
import { ReactQueryKey } from "../../../../../../types";
import { FormTextField } from "../../../../../../components/fields";
import { getCarGroupCode } from "../../../../../../utils";
import { useRecoilValue } from "recoil";
import { editPendingBookingRce } from "../../../../recoil";
export const FormFcaBankCardNumber = () => {

    const { t } = useTranslation('booking');

    const formContext = useFormContext<IFormValues>();

    const isPendingBookingRce = useRecoilValue<boolean | undefined>(editPendingBookingRce);

    const fcaBankCardNumber = useWatch<IFormValues, 'extraServices.fcaBankCardNumber'>({
        name: 'extraServices.fcaBankCardNumber'
    });
    const pickupLocation = useWatch<IFormValues, 'basicDetails.pickupLocation'>({
        name: 'basicDetails.pickupLocation'
    });
    const dropoffLocation = useWatch<IFormValues, 'basicDetails.dropoffLocation'>({
        name: 'basicDetails.dropoffLocation'
    });
    const pickupDate = useWatch<IFormValues, 'basicDetails.pickupDate'>({
        name: 'basicDetails.pickupDate'
    });

    const dropoffDate = useWatch<IFormValues, 'basicDetails.dropoffDate'>({
        name: 'basicDetails.dropoffDate'
    });

    const carGroup = useWatch<IFormValues, 'basicDetails.carGroup'>({
        name: 'basicDetails.carGroup'
    });

    const rentalType = useWatch<IFormValues, 'basicDetails.rentalType'>({
        name: 'basicDetails.rentalType'
    });
    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenant" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealer" });

    const productsQuery = useQuery({
        queryKey: [ReactQueryKey.Product, {segment: rentalType, tenant: tenant as string, dealer: dealer as string}],
        queryFn: () => getProduct({segment: rentalType, businessContinuityInsToolMode: false, tenant: tenant as string, dealer: dealer as string}),
        enabled: false
    });

    const queryClient = useQueryClient();

    const couponQuery = useCouponQuery(
        {
            pickUpDate: pickupDate?.isValid() ? pickupDate.toISOString() : undefined,
            dropoffDate: dropoffDate?.isValid() ? dropoffDate.toISOString() : undefined,
            pickUpLocation: pickupLocation?.id,
            dropOffLocation: dropoffLocation?.id,
            promoCode: fcaBankCardNumber,
            carGroupCode: !!carGroup ? getCarGroupCode(carGroup) : undefined,
            productCodes: !!productsQuery?.data?.products ? productsQuery.data.products.map(elem => {return elem.code}) : undefined,
            tenant: tenant as string,
            dealer: dealer as string
        },
        {
            enabled: false,
            onSuccess: (data) => {
                if (data?.code !== RESPONSE_CHECK_CODE.SUCCESS) {
                    formContext.setError('extraServices.fcaBankCardNumber', {
                        message: 'message.validation.invalid_code'
                    });
                    return;
                }
                formContext.clearErrors('extraServices.fcaBankCardNumber');
            }
        }
    );

    const handleCheck = useCallback(() => {
        couponQuery.refetch();
    }, []);
    const handleKeyDown = useCallback(() => {
        queryClient.resetQueries([ReactQueryKey.Coupon, {tenant: tenant as string, dealer: dealer as string}]);
        if (formContext.getFieldState('extraServices.fcaBankCardNumber').error) {
            formContext.clearErrors('extraServices.fcaBankCardNumber');
        }
    }, []);

    const EndAdornemnt = useMemo(() => {
        if (
            !fcaBankCardNumber ||
            !pickupDate ||
            !dropoffDate ||
            !pickupLocation ||
            !dropoffLocation ||
            !carGroup||
            !productsQuery?.data?.products
        ) {
            return undefined;
        }
        if (couponQuery.isFetching) {
            return <CircularProgress size={20} />
        }
        if (!couponQuery.data) {
            return (
                <InputAdornment position="end">
                    <IconButton
                        onClick={handleCheck}
                    >
                        <SendRounded />
                    </IconButton>
                </InputAdornment>
            )
        }
        if (couponQuery.data.code !== RESPONSE_CHECK_CODE.SUCCESS) {
            return (
                <HighlightOffRounded
                    color="error"
                />
            );
        }
        return (
            <CheckCircleOutlineRounded
                color="success"
            />
        );
    }, [
        fcaBankCardNumber,
        pickupDate,
        dropoffDate,
        pickupLocation,
        dropoffLocation,
        carGroup,
        couponQuery,
        productsQuery?.data?.products
    ]);

    return (
        <FormTextField<IFormValues>
            name="extraServices.fcaBankCardNumber"
            label={t('label.fcaBankCardNumber') + '*'}
            disabled={isPendingBookingRce || couponQuery.isFetching}
            InputProps={{
                endAdornment: EndAdornemnt
            }}
            onKeyDown={handleKeyDown}
        />
    );
};

export default FormFcaBankCardNumber;
